// helpers.tsx
// Utility and rendering helpers for the OrderTable component.
// Provides reusable functions for sorting, rendering patient/insurance info, and formatting table data.

import { SortOrder } from "@/types/order-table";
import { ProcedureTable, RowData } from "@/types/my-tasks";
import PatientName from "@/components/PatientName";
import { styled } from "@stitches/react";
import { Insurance } from "@/types/procedure";
import { AlertIcon, AttentionIcon } from "../Icons";
import { Tooltip } from "@/vendor/Forge/dist";
import { formatDate } from "@/utils/helpers";

/**
 * Renders the patient name for a table row, using Athena if available, otherwise Cerner.
 * Highlights if Athena data is missing.
 * @param _name - Unused, present for signature compatibility
 * @param rowData - Row data containing patient details
 * @returns PatientName component
 */
export const renderPatientName = (
  _name: string,
  { rowData }: RowData<ProcedureTable>,
) => (
  <PatientName
    name={
      rowData?.athenaPatientDetails?.patientName ??
      rowData?.cernerPatientDetails?.patientName
    }
    isMissingAthenaData={!rowData.athenaPatientDetails}
  />
);

/**
 * Gets the current sort order for a column.
 * @param sortColumn - The column to check
 * @param currentSortColumn - The currently sorted column
 * @param sortOrder - The current sort order
 * @returns The sort order if the column matches, otherwise null
 */
export const getSortOrder = (
  sortColumn: string,
  currentSortColumn?: string,
  sortOrder?: SortOrder,
) => (currentSortColumn !== sortColumn ? null : sortOrder);

/**
 * Determines the new sort order for a column when toggled.
 * @param sortColumn - The column to sort
 * @param currentSortColumn - The currently sorted column
 * @param sortOrder - The current sort order
 * @returns 'ASC' or 'DESC' depending on toggle logic
 */
export const getNewSortOrder = (
  sortColumn: string,
  currentSortColumn?: string,
  sortOrder?: SortOrder,
) => {
  if (currentSortColumn !== sortColumn) return "ASC";
  return sortOrder === "DESC" || !sortOrder ? "ASC" : "DESC";
};

// Wrapper for insurance display
const Wrapper = styled("div", {
  display: "flex",
  gap: 10,
  alignItems: "center",
});

/**
 * Renders insurance information for a patient.
 * If no insurance, shows a tooltip prompting update.
 * @param insurance - Array of insurance objects
 * @returns Insurance display or tooltip
 */
export const renderInsurance = (insurance: Insurance[]) => {
  const InsuranceTextWrapper = styled("div", {
    display: "flex",
    flexDirection: "column",
    gap: 5,
  });

  return insurance.length ? (
    <InsuranceTextWrapper>
      {insurance.map((ins) => (
        <Wrapper key={ins.insuranceNumber}>
          {ins.insurancePlanDisplayName}
        </Wrapper>
      ))}
    </InsuranceTextWrapper>
  ) : (
    <Tooltip
      id="tool-insurance-unavailable"
      text="Patient is missing insurance information. Please select an insurance for this patient in Case Details."
    >
      <Wrapper>
        <AttentionIcon />
        Update details
      </Wrapper>
    </Tooltip>
  );
};

/**
 * Renders the surgery date for a procedure, with special handling for emergencies.
 * @param procedure - The procedure row data
 * @returns Formatted date or tooltip for emergencies
 */
export const renderSurgeryDate = (procedure: ProcedureTable) => {
  if (!procedure.emergency)
    return formatDate({ date: procedure.surgeryDate, format: "ff" });
  return (
    <Tooltip
      id="tool-surgery-emergent"
      text="This case requires immediate attention. The surgery date has either passed or is within the next 48 hours."
    >
      <Wrapper>
        <AlertIcon />
        {formatDate({ date: procedure.surgeryDate, format: "ff" })}
      </Wrapper>
    </Tooltip>
  );
};

/**
 * Checks if an auth status is valid for filtering.
 * @param status - The status string
 * @returns True if valid, false if 'All' or 'Completed'
 */
export const isValidAuthStatus = (status: string) =>
  status !== "All" && status !== "Completed";
